import { useEffect, useMemo, useState } from 'react';
import { Card, Title, Text, Flex, BarList, Dialog, DialogPanel } from '@tremor/react';
import { RangeSelectChart } from '../components/RangeSelectChart';
import { ErrorBanner } from '../components/ErrorBanner';
import {
  usePlatformPerfSummary,
  usePlatformPerfTimeseries,
  usePlatformPerfPerRequest,
  usePlatformPerfByClassifierCount,
  usePlatformPerfByClassifier,
  usePlatformPerfHedge,
  usePlatformPerfTopSlow,
  usePlatformPerfLookup,
  usePlatformPerfRequestDetail,
  type PlatformPerfParams,
} from '../hooks/usePlatformPerfStats';
import type {
  PlatformPerfHedgeResponse,
  PlatformPerfHedgeByClassifier,
  PlatformPerfPerRequestResponse,
  PlatformPerfRequestDetail,
  PlatformPerfLookupResponse,
} from '../types';

type Preset = '1h' | '24h' | '7d' | '30d' | 'custom';
type Granularity = 'auto' | 'hour' | 'day';

const PRESETS: { value: Preset; label: string }[] = [
  { value: '1h', label: 'Last 1h' },
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: 'custom', label: 'Custom' },
];

const PRESET_MS: Record<Exclude<Preset, 'custom'>, number> = {
  '1h': 3600_000,
  '24h': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
};

// Hedge delays are pulled live from process.env.OPENAI_HEDGE_DELAYS_MS via the
// /api/stats/platform-perf/hedge endpoint (which calls classifier.service.js's
// getHedgeDelays()). When the page first loads (no hedge data yet) we fall back
// to common defaults so the static help text reads correctly.
const FALLBACK_HEDGE_DELAYS = [600, 1800];
function delayAt(delays: number[], i: number): number {
  return delays[i] ?? FALLBACK_HEDGE_DELAYS[i] ?? 0;
}
function fmtDelays(delays: number[]): string {
  if (!delays.length) return 'disabled';
  return delays.map((d) => `${d}ms`).join(' / ');
}

function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

function fmtPercent(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtMs(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(2)}s`;
  return `${Math.round(n)}ms`;
}

function fmtBucket(iso: string, granularity: 'hour' | 'day'): string {
  const d = new Date(iso);
  if (granularity === 'hour') {
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtShortId(id: string | null): string {
  if (!id) return '—';
  return id.length > 8 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

function roundUpTo(n: number | null | undefined, step: number): number | null {
  if (n === null || n === undefined) return null;
  return Math.max(step, Math.ceil(n / step) * step);
}

export function MonitoringPage() {
  const [preset, setPreset] = useState<Preset>('24h');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [granularity, setGranularity] = useState<Granularity>('auto');
  const [orgId, setOrgId] = useState('');
  const [userId, setUserId] = useState('');

  const params = useMemo<(PlatformPerfParams & { granularity?: Granularity }) | null>(() => {
    let fromIso: string;
    let toIso: string;
    if (preset === 'custom') {
      if (!from || !to) return null;
      fromIso = new Date(from).toISOString();
      toIso = new Date(to).toISOString();
    } else {
      const now = Date.now();
      toIso = new Date(now).toISOString();
      fromIso = new Date(now - PRESET_MS[preset]).toISOString();
    }
    return {
      from: fromIso,
      to: toIso,
      granularity,
      orgId: orgId.trim() || undefined,
      userId: userId.trim() || undefined,
    };
  }, [preset, from, to, granularity, orgId, userId]);

  const summary = usePlatformPerfSummary(params);
  const timeseries = usePlatformPerfTimeseries(params);
  const perRequest = usePlatformPerfPerRequest(params);
  const byClassifierCount = usePlatformPerfByClassifierCount(params);
  const byClassifier = usePlatformPerfByClassifier(params);
  const hedge = usePlatformPerfHedge(params);
  const topSlow = usePlatformPerfTopSlow(params ? { ...params, limit: 20 } : null);
  const lookup = usePlatformPerfLookup(orgId.trim() || undefined, userId.trim() || undefined);

  const [detailId, setDetailId] = useState<string | null>(null);

  const granularityResolved = timeseries.data?.granularity ?? 'hour';
  const hedgeDelays = hedge.data?.hedgeDelaysMs ?? FALLBACK_HEDGE_DELAYS;
  const hedgeEnabled = hedge.data?.hedgeEnabled ?? true;

  const seriesData = useMemo(() => {
    if (!timeseries.data) return [];
    return timeseries.data.series.map((b) => ({
      bucket: fmtBucket(b.bucket, granularityResolved),
      fetchP95: b.fetchP95 ?? null,
      reflectionCount: b.reflectionCount ?? 0,
      fetchCount: b.fetchCount ?? 0,
    }));
  }, [timeseries.data, granularityResolved]);

  // Zoom state — shared brush window across timeseries charts.
  const [zoomRange, setZoomRange] = useState<{ start: number; end: number } | null>(null);
  useEffect(() => {
    setZoomRange(null);
  }, [preset, from, to, granularity, orgId, userId]);
  const fullLen = seriesData.length;
  const zoomStart = zoomRange ? Math.max(0, Math.min(zoomRange.start, fullLen - 1)) : 0;
  const zoomEnd = zoomRange ? Math.max(zoomStart, Math.min(zoomRange.end, fullLen - 1)) : Math.max(0, fullLen - 1);
  const isZoomed = zoomRange !== null && (zoomStart > 0 || zoomEnd < fullLen - 1);
  const slicedSeries = useMemo(() => {
    if (fullLen === 0) return [];
    return seriesData.slice(zoomStart, zoomEnd + 1);
  }, [seriesData, zoomStart, zoomEnd, fullLen]);
  const handleZoom = (start: number, end: number) => {
    if (start === 0 && end === fullLen - 1) setZoomRange(null);
    else setZoomRange({ start, end });
  };
  const resetZoom = () => setZoomRange(null);

  return (
    <div className="space-y-6">
      {/* Header / controls */}
      <Card className="bg-gray-900 ring-gray-800">
        <Flex justifyContent="between" alignItems="center" className="flex-wrap gap-3">
          <div>
            <Title className="text-gray-100">HarmCheck Platform API — performance</Title>
            <Text className="text-gray-500 text-xs mt-1 max-w-3xl">
              Diagnoses "why is the Platform API slow?" by correlating two layers logged in
              iconect-jobs's own Mongo:
              <span className="text-cyan-400"> Reflection</span> (one row per POST /v1/reflections,
              one text block) →
              <span className="text-amber-400"> FetchLog</span> (one row per upstream attempt to
              OpenAI / Anthropic / Phi — hedge attempts each write a row).
              Compared to the Outlook page there's one less fan-out level: a Reflection classifies
              ONE text through M classifiers, not N sentences × M classifiers.
              The FK <span className="font-mono">FetchLog.reflectionId</span> ties children to
              parent for proper per-request rollups (historical rows pre-FK are excluded from those views).
            </Text>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as Preset)}
              className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-sm rounded px-2 py-1"
            >
              {PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {preset === 'custom' && (
              <>
                <input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-sm rounded px-2 py-1"
                />
                <span className="text-gray-500 text-sm">→</span>
                <input
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-sm rounded px-2 py-1"
                />
              </>
            )}
            <div className="flex gap-1 rounded-md ring-1 ring-gray-800 p-1">
              {(['auto', 'hour', 'day'] as Granularity[]).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGranularity(g)}
                  className={`px-2 py-0.5 text-xs rounded ${
                    granularity === g ? 'bg-gray-700 text-gray-100' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </Flex>
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              placeholder="Org filter — ObjectId, slug, or name"
              title="Type an org's ObjectId (24 hex chars), slug, or name. Resolved against iconect-jobs's organizations collection."
              className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-xs rounded px-2 py-1 w-72 font-mono"
            />
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="User filter — ObjectId or email"
              title="Type a user's ObjectId (24 hex chars) or email. Resolved against iconect-jobs's users collection."
              className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-xs rounded px-2 py-1 w-72 font-mono"
            />
          </div>
          <FilterLookupHint orgInput={orgId.trim()} userInput={userId.trim()} lookup={lookup.data} />
        </div>
        {summary.data && (
          <Text className="text-gray-500 text-xs mt-2">
            {new Date(summary.data.range.from).toLocaleString()} →{' '}
            {new Date(summary.data.range.to).toLocaleString()} · granularity {granularityResolved}
          </Text>
        )}
      </Card>

      {/* KPI strip — per-row percentiles. The hints flag that "p95 per fetch
          attempt" is NOT per-request (includes losing hedges); the
          per-request rollup card later is the apples-to-apples view. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi
          label="Reflections"
          value={fmtNumber(summary.data?.reflection?.count)}
          hint={`${fmtPercent(summary.data?.reflection?.harmfulRate)} harmful`}
        />
        <Kpi
          label="Fetch attempts"
          value={fmtNumber(summary.data?.fetch?.count)}
          hint={`${fmtPercent(summary.data?.fetch?.errorRate)} errors`}
        />
        <Kpi
          label="p95 per fetch attempt"
          value={fmtMs(summary.data?.fetch?.p95)}
          hint={`p50 ${fmtMs(summary.data?.fetch?.p50)} · p99 ${fmtMs(summary.data?.fetch?.p99)} · includes losing hedges`}
        />
        <Kpi
          label="Tokens"
          value={fmtNumber(summary.data?.fetch?.totalTokens)}
          hint={`avg ${fmtMs(summary.data?.fetch?.avgMs)} per attempt`}
        />
        <Kpi
          label="Orchestration overhead"
          value={fmtMs(summary.data?.orchestrationOverhead?.p50)}
          hint={`median (p95 ${fmtMs(summary.data?.orchestrationOverhead?.p95)}) · request time − slowest fetch`}
        />
      </div>

      {/* Layer latency over time */}
      <Card className="bg-gray-900 ring-gray-800">
        <Flex justifyContent="between" alignItems="center" className="mb-1">
          <Title className="text-gray-100">Fetch latency over time (p95)</Title>
          {isZoomed && (
            <button
              type="button"
              onClick={resetZoom}
              className="text-xs px-2 py-1 rounded ring-1 ring-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
            >
              Reset zoom
            </button>
          )}
        </Flex>
        <Text className="text-gray-500 text-xs mb-2">
          p95 latency of upstream fetch attempts over time. Spikes here are the upstream tail —
          OpenAI / Anthropic / Phi getting slow.{' '}
          <span className="text-gray-400">Drag the handles at the bottom to zoom in on a range.</span>
        </Text>
        {timeseries.error && !timeseries.data ? (
          <ErrorBanner error={timeseries.error} />
        ) : timeseries.isLoading && !timeseries.data ? (
          <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
        ) : seriesData.length === 0 ? (
          <Text className="text-gray-500 text-sm">No activity in this range.</Text>
        ) : (
          <RangeSelectChart
            data={seriesData}
            xKey="bucket"
            series={[{ key: 'fetchP95', label: 'Fetch p95', color: 'amber' }]}
            valueFormatter={fmtMs}
            syncId="platform-perf-ts"
            showBrush
            brushStart={zoomStart}
            brushEnd={zoomEnd}
            onBrushChange={handleZoom}
            heightClass="h-56"
            hideLegend
          />
        )}
      </Card>

      {/* Volume by layer — 2 charts (one less fan-out than Outlook page). */}
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Volume by layer</Title>
        <Text className="text-gray-500 text-xs mb-3">
          Each Reflection fans out into N FetchLogs (one per classifier × hedge attempts). The two
          axes use independent y-scales — Reflection count and FetchLog count differ in magnitude.
        </Text>
        {timeseries.error && !timeseries.data ? (
          <ErrorBanner error={timeseries.error} />
        ) : seriesData.length === 0 ? (
          <Text className="text-gray-500 text-sm">No activity in this range.</Text>
        ) : (
          <div className="space-y-3">
            <VolumeSubChart
              label="Reflections (POST /v1/reflections)"
              data={slicedSeries}
              seriesKey="reflectionCount"
              seriesLabel="Reflections"
              color="cyan"
            />
            <VolumeSubChart
              label="FetchLogs (upstream attempts — includes hedges)"
              data={slicedSeries}
              seriesKey="fetchCount"
              seriesLabel="FetchLogs"
              color="amber"
            />
          </div>
        )}
      </Card>

      {/* Per-request layer time */}
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Per-request layer time</Title>
        <Text className="text-gray-500 text-xs mb-3">
          For each Reflection, child FetchLog rows are summed (and max'd) up to the parent. Powered
          by the <span className="font-mono">FetchLog.reflectionId</span> FK. Requests before that
          FK shipped don't appear here, but everything else (timeseries, by-classifier, hedge) still
          covers them.
          <span className="block mt-1">
            <b>Request ms</b> = real <span className="font-mono">Reflection.requestMs</span> when
            saved by the route handler, falling back to (last child FetchLog.createdAt) −
            (Reflection.createdAt) for historical rows. <b>Fetch sum</b> intentionally
            overcounts hedge attempts; read it as "total upstream work", not wall time.
          </span>
        </Text>
        <PerRequestTable
          data={perRequest.data}
          error={perRequest.error}
          isLoading={perRequest.isLoading}
        />
        {perRequest.data && (
          <div className="mt-3 text-xs text-gray-500 space-y-1">
            <div>
              <b className="text-gray-400">Fan-out</b> — classifiers per request: p50{' '}
              <span className="text-gray-300">{fmtNumber(perRequest.data.classifierCountPerRequest.p50)}</span>
              {' · '}p95{' '}
              <span className="text-gray-300">{fmtNumber(perRequest.data.classifierCountPerRequest.p95)}</span>
              {' · fetches per request: p50 '}
              <span className="text-gray-300">{fmtNumber(perRequest.data.fetchCountPerRequest.p50)}</span>
              {' · p95 '}
              <span className="text-gray-300">{fmtNumber(perRequest.data.fetchCountPerRequest.p95)}</span>
              . fetchCount &gt; classifierCount means hedges are firing.
            </div>
            <div>
              <b className="text-gray-400">Payload size</b> — text length (chars): p50{' '}
              <span className="text-gray-300">{fmtNumber(perRequest.data.textLengthPerRequest.p50)}</span>
              {' · '}p95{' '}
              <span className="text-gray-300">{fmtNumber(perRequest.data.textLengthPerRequest.p95)}</span>
              {' · '}p99{' '}
              <span className="text-gray-300">{fmtNumber(perRequest.data.textLengthPerRequest.p99)}</span>
              . Larger text = more OpenAI tokens, usually slower.
            </div>
            <div>
              <b className="text-gray-400">Client lag</b> (browser → server, from{' '}
              <span className="font-mono">x-client-start</span> header): p50{' '}
              <span className="text-gray-300">{fmtMs(perRequest.data.clientLagPerRequest.p50)}</span>
              {' · '}p95{' '}
              <span className="text-gray-300">{fmtMs(perRequest.data.clientLagPerRequest.p95)}</span>
              {' · '}p99{' '}
              <span className="text-gray-300">{fmtMs(perRequest.data.clientLagPerRequest.p99)}</span>
              . Only present when the caller sets the header — high values mean network/wakeup,
              not backend.
            </div>
            <div className="text-gray-500">
              n = {fmtNumber(perRequest.data.requestsWithFetches)} requests with FK-joined fetches
              {' '}(of {fmtNumber(perRequest.data.requests)} total).{' '}
              {fmtNumber(perRequest.data.requestsWithRealMs)} use the real{' '}
              <span className="font-mono">Reflection.requestMs</span> field; the rest fall back
              to the (last fetch − reflection.createdAt) proxy.
            </div>
          </div>
        )}
      </Card>

      {/* Latency by classifier count */}
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Latency by classifier count</Title>
        <Text className="text-gray-500 text-xs mb-3">
          Reflections bucketed by how many distinct classifiers ran (= bundle size for that
          request). p95 = the headline column — if it rises with bucket size, slowness is driven
          by larger bundles. "ms / classifier" tells you whether each classifier itself gets
          more expensive as N grows (concurrency / backpressure within one request).
        </Text>
        {byClassifierCount.error && !byClassifierCount.data ? (
          <ErrorBanner error={byClassifierCount.error} />
        ) : byClassifierCount.data && byClassifierCount.data.buckets.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-xs">
                <tr>
                  <th className="text-left py-1 pr-3" title="Classifier count bucket — how many distinct classifiers ran for the request.">Classifiers</th>
                  <th className="text-right py-1 px-3" title="Number of Reflections that landed in this bucket.">Requests</th>
                  <th className="text-right py-1 px-3" title="50th percentile of Reflection request time (last child FetchLog.createdAt − Reflection.createdAt).">p50 ms</th>
                  <th className="text-right py-1 px-3" title="95th percentile of Reflection request time. The headline — if it climbs with bundle size, larger bundles drive slowness.">p95 ms</th>
                  <th className="text-right py-1 px-3" title="99th percentile of Reflection request time.">p99 ms</th>
                  <th className="text-right py-1 px-3" title="Mean Reflection request time for this bucket.">avg ms</th>
                  <th className="text-right py-1 px-3" title="Per-classifier cost — total time divided by total classifiers across this bucket. If this rises with bucket size there's backpressure within one request.">ms / classifier</th>
                </tr>
              </thead>
              <tbody className="text-gray-200">
                {byClassifierCount.data.buckets.map((b) => (
                  <tr key={b.bucketKey} className="border-t border-gray-800">
                    <td className="py-1 pr-3 font-mono text-xs">{b.label}</td>
                    <td className="py-1 px-3 text-right">{fmtNumber(b.count)}</td>
                    <td className="py-1 px-3 text-right">{fmtMs(b.p50)}</td>
                    <td className="py-1 px-3 text-right">{fmtMs(b.p95)}</td>
                    <td className="py-1 px-3 text-right">{fmtMs(b.p99)}</td>
                    <td className="py-1 px-3 text-right">{fmtMs(b.avgMs)}</td>
                    <td className="py-1 px-3 text-right">{fmtMs(b.avgMsPerClassifier)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Text className="text-gray-500 text-sm">No requests in this range.</Text>
        )}
      </Card>

      {/* Per-classifier breakdown */}
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Upstream fetches — by classifier</Title>
        <Text className="text-gray-500 text-xs mb-3">
          Every classifier key that ran in this window. <b>Wall p95 (winner-only)</b> from the
          hedge table below; <b>All p50/p95</b> here include every attempt (losing hedges too).
          Sort: most-called first.
        </Text>
        {byClassifier.error && !byClassifier.data ? (
          <ErrorBanner error={byClassifier.error} />
        ) : byClassifier.data && byClassifier.data.byClassifier.length > 0 ? (
          <>
            <BarList
              data={byClassifier.data.byClassifier.map((r) => ({
                name: `${r.classifierKey} (${r.service}) · all p95 ${fmtMs(r.p95)} · err ${fmtPercent(r.errorRate)}`,
                value: r.count,
              }))}
              valueFormatter={fmtNumber}
            />
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-500 text-xs">
                  <tr>
                    <th className="text-left py-1 pr-3" title="FetchLog.classifierKey — the classifier identifier (e.g. 'sentiment', 'race').">Classifier</th>
                    <th className="text-left py-1 px-3" title="FetchLog.service — which backend ran this classifier (openai / anthropic / phi).">Service</th>
                    <th className="text-right py-1 px-3" title="Total successful attempts in the window (every row that wrote a FetchLog), including losing hedges.">Calls</th>
                    <th className="text-right py-1 px-3" title="50th percentile of FetchLog.durationMs across every attempt — includes hedge inflation.">p50</th>
                    <th className="text-right py-1 px-3" title="95th percentile across every attempt. Compare to Wall p95 in the hedge table for the user-perceived number.">p95</th>
                    <th className="text-right py-1 px-3" title="99th percentile across every attempt.">p99</th>
                    <th className="text-right py-1 px-3" title="Mean attempt duration.">avg</th>
                    <th className="text-right py-1 px-3" title="Percent of attempts that failed (success: false).">Err %</th>
                    <th className="text-right py-1 px-3" title="Total tokens consumed (usage.total_tokens summed across attempts).">Tokens</th>
                  </tr>
                </thead>
                <tbody className="text-gray-200">
                  {byClassifier.data.byClassifier.map((r) => (
                    <tr key={`${r.classifierKey}-${r.service}`} className="border-t border-gray-800">
                      <td className="py-1 pr-3 font-mono text-xs">{r.classifierKey}</td>
                      <td className="py-1 px-3 font-mono text-xs">{r.service}</td>
                      <td className="py-1 px-3 text-right">{fmtNumber(r.count)}</td>
                      <td className="py-1 px-3 text-right">{fmtMs(r.p50)}</td>
                      <td className="py-1 px-3 text-right">{fmtMs(r.p95)}</td>
                      <td className="py-1 px-3 text-right">{fmtMs(r.p99)}</td>
                      <td className="py-1 px-3 text-right">{fmtMs(r.avgMs)}</td>
                      <td className="py-1 px-3 text-right">{fmtPercent(r.errorRate)}</td>
                      <td className="py-1 px-3 text-right">{fmtNumber(r.tokens)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <Text className="text-gray-500 text-sm">No fetches in this range.</Text>
        )}
      </Card>

      {/* Hedge performance */}
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Hedge / speculative-retry performance</Title>
        <Text className="text-gray-500 text-xs mb-3">
          iconect-jobs's classifier service does <b>tied request hedging</b>: the primary attempt
          fires immediately; hedge attempts fire at delays configured via
          <span className="font-mono"> OPENAI_HEDGE_DELAYS_MS</span> (live value:{' '}
          <span className="font-mono">{fmtDelays(hedgeDelays)}</span>) IF no winner has resolved.
          The first to complete wins; losing in-flight attempts are NOT cancelled. Below: how
          often the hedge fires per classifier, who wins, and estimated wall-time savings.
          {!hedgeEnabled && (
            <span className="block mt-1 text-amber-400">
              Hedging is currently disabled (OPENAI_HEDGE_DELAYS_MS unset). Tuning suggestions
              below use fallback defaults of 600 / 1800ms.
            </span>
          )}
        </Text>
        <HedgeSummary data={hedge.data} error={hedge.error} isLoading={hedge.isLoading} delays={hedgeDelays} />
        {hedge.data && hedge.data.byClassifier.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-xs">
                <tr>
                  <th className="text-left py-1 pr-3" title="Classifier key — one row per upstream classifier.">Classifier</th>
                  <th className="text-left py-1 px-3" title="Backend service (openai / anthropic / phi).">Service</th>
                  <th className="text-right py-1 px-3" title="Logical fetches — one per (reflection, classifier) tuple. Each may spawn 1, 2, or 3 attempts under the hedge race.">Logical fetches</th>
                  <th className="text-right py-1 px-3" title="% of logical fetches where the hedge fired (attempts > 1).">Hedge fire %</th>
                  <th className="text-right py-1 px-3" title="Mean attempts per logical fetch. 1.00 = hedge never fires; closer to 2 or 3 = hedges fire frequently.">Avg attempts</th>
                  <th className="text-right py-1 px-3" title="% of logical fetches whose winner was the primary attempt. Higher is better.">Won by #1</th>
                  <th className="text-right py-1 px-3" title="% of logical fetches whose winner was hedge attempt #2.">Won by #2</th>
                  <th className="text-right py-1 px-3" title="% of logical fetches whose winner was hedge attempt #3.">Won by #3</th>
                </tr>
              </thead>
              <tbody className="text-gray-200">
                {hedge.data.byClassifier.map((r) => (
                  <tr key={r.classifierKey} className="border-t border-gray-800">
                    <td className="py-1 pr-3 font-mono text-xs">{r.classifierKey}</td>
                    <td className="py-1 px-3 font-mono text-xs">{r.service ?? '—'}</td>
                    <td className="py-1 px-3 text-right">{fmtNumber(r.groups)}</td>
                    <td className="py-1 px-3 text-right">{fmtPercent(r.hedgeFireRate)}</td>
                    <td className="py-1 px-3 text-right">{r.avgAttempts?.toFixed(2) ?? '—'}</td>
                    <td className="py-1 px-3 text-right">{fmtPercent(r.winnerByAttemptPct[1])}</td>
                    <td className="py-1 px-3 text-right">{fmtPercent(r.winnerByAttemptPct[2])}</td>
                    <td className="py-1 px-3 text-right">{fmtPercent(r.winnerByAttemptPct[3])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Hedge tuning guidance */}
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Hedge tuning guidance</Title>
        <Text className="text-gray-500 text-xs mb-3">
          <b>The tradeoff:</b> shorter first delay catches the slow tail sooner (better p95 / p99
          for users) but fires hedges more often (more upstream load + tokens cost). The
          Dean &amp; Barroso <i>Tail at Scale</i> rule: set delay near winner p95 so hedge fires
          ~5% of the time.
          <span className="block mt-1">
            <b>Where the delays live:</b>{' '}
            <span className="font-mono">classifier.service.js</span> reads env{' '}
            <span className="font-mono">OPENAI_HEDGE_DELAYS_MS</span>. Live value:{' '}
            <span className="font-mono">{fmtDelays(hedgeDelays)}</span>.
          </span>
        </Text>
        <HedgeTuningTable data={hedge.data} error={hedge.error} isLoading={hedge.isLoading} delays={hedgeDelays} />
        <div className="mt-4 text-xs text-gray-500 space-y-1">
          <div><b className="text-gray-400">Read the table:</b></div>
          <ul className="list-disc pl-5 space-y-1">
            <li><b className="text-emerald-400">Healthy</b> — 2–10% fire, winnerP95 under delay1, mostly attempt-1 wins.</li>
            <li><b className="text-amber-400">Fires too often</b> — &gt;15% fire. Raise delay1 toward p95+margin.</li>
            <li><b className="text-amber-400">Wasted hedges</b> — fires but rarely wins. Raise delay1 toward p99.</li>
            <li><b className="text-rose-400">Tail escapes the hedge</b> — winnerP99 &gt; delay2. Lower delay2 or investigate upstream.</li>
            <li>Suggestions = winnerP95 rounded up to 50ms (delay1), winnerP99 rounded up to 100ms (delay2).</li>
          </ul>
        </div>
      </Card>

      {/* Slowest requests */}
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Slowest Reflections</Title>
        <Text className="text-gray-500 text-xs mb-3">
          Top 20 in window by request time (= last child FetchLog.createdAt − Reflection.createdAt).
          Only requests with at least one FK-joined FetchLog appear. <b>Click a row</b> for the
          full breakdown including every hedge attempt per classifier.
        </Text>
        {topSlow.error && !topSlow.data ? (
          <ErrorBanner error={topSlow.error} />
        ) : topSlow.data && topSlow.data.requests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-xs">
                <tr>
                  <th className="text-left py-1 pr-3" title="Reflection.createdAt — when the POST landed.">When</th>
                  <th className="text-left py-1 px-3" title="Reflection.bundleKey — which classifier bundle was used.">Bundle</th>
                  <th className="text-left py-1 px-3" title="Truncated organizationId / userId. Paste either (or org slug / user email) into the filters above to scope.">Org / User</th>
                  <th className="text-right py-1 px-3" title="Approximated request time = last child FetchLog.createdAt − Reflection.createdAt.">Request ms</th>
                  <th className="text-right py-1 px-3" title="Slowest single FetchLog.durationMs for this request.">Max fetch</th>
                  <th className="text-right py-1 px-3" title="Distinct classifier keys that ran for this request (= bundle size).">Classifiers</th>
                  <th className="text-right py-1 px-3" title="Total FetchLog rows for this request (includes hedge attempts).">Fetches</th>
                  <th className="text-right py-1 px-3" title="Length of the text body in characters.">Chars</th>
                  <th className="text-left py-1 px-3" title="The single FetchLog row with the highest durationMs — classifier key plus duration.">Slowest fetch</th>
                  <th className="text-left py-1 pl-3" title="Reflection._id — click the row to open the full breakdown.">reflectionId</th>
                </tr>
              </thead>
              <tbody className="text-gray-200">
                {topSlow.data.requests.map((r) => (
                  <tr
                    key={r.reflectionId}
                    onClick={() => setDetailId(r.reflectionId)}
                    className="border-t border-gray-800 cursor-pointer hover:bg-gray-800/40"
                  >
                    <td className="py-1 pr-3 whitespace-nowrap">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="py-1 px-3 font-mono text-xs">{r.bundleKey ?? '—'}</td>
                    <td className="py-1 px-3 font-mono text-xs">
                      {fmtShortId(r.orgId)} / {fmtShortId(r.userId)}
                    </td>
                    <td className="py-1 px-3 text-right">{fmtMs(r.requestMs)}</td>
                    <td className="py-1 px-3 text-right">{fmtMs(r.maxFetchMs)}</td>
                    <td className="py-1 px-3 text-right">{fmtNumber(r.classifierCount)}</td>
                    <td className="py-1 px-3 text-right">{fmtNumber(r.fetchCount)}</td>
                    <td className="py-1 px-3 text-right">{fmtNumber(r.textLength)}</td>
                    <td className="py-1 px-3 font-mono text-xs">
                      {r.slowestFetch
                        ? `${r.slowestFetch.classifierKey ?? '—'} · ${fmtMs(r.slowestFetch.durationMs)}`
                        : '—'}
                    </td>
                    <td className="py-1 pl-3 font-mono text-[10px] text-gray-500">{r.reflectionId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Text className="text-gray-500 text-sm">No requests in this range.</Text>
        )}
      </Card>

      <RequestDetailModal reflectionId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card className="bg-gray-900 ring-gray-800 p-4">
      <Text className="text-gray-400 text-xs">{label}</Text>
      <p className="text-2xl font-semibold text-gray-100 mt-1">{value}</p>
      <Text className="text-gray-500 text-xs mt-1">{hint}</Text>
    </Card>
  );
}

function VolumeSubChart({
  label,
  data,
  seriesKey,
  seriesLabel,
  color,
}: {
  label: string;
  data: Array<Record<string, unknown>>;
  seriesKey: string;
  seriesLabel: string;
  color: 'cyan' | 'amber' | 'violet';
}) {
  return (
    <div>
      <Text className="text-gray-400 text-xs mb-1">{label}</Text>
      <RangeSelectChart
        data={data}
        xKey="bucket"
        series={[{ key: seriesKey, label: seriesLabel, color }]}
        valueFormatter={fmtNumber}
        syncId="platform-perf-ts"
        heightClass="h-28"
        hideLegend
      />
    </div>
  );
}

function FilterLookupHint({
  orgInput,
  userInput,
  lookup,
}: {
  orgInput: string;
  userInput: string;
  lookup: PlatformPerfLookupResponse | null;
}) {
  if (!orgInput && !userInput) return null;
  const parts: React.ReactNode[] = [];
  if (orgInput) {
    const o = lookup?.org;
    parts.push(
      <span key="org">
        <span className="text-gray-400">Org:</span>{' '}
        {o ? (
          <span>
            <span className="text-emerald-400">{o.name ?? '(unnamed)'}</span>
            {o.slug && <span className="text-gray-400"> · {o.slug}</span>}
            <span className="text-gray-500"> → {o._id.slice(0, 8)}…</span>
          </span>
        ) : (
          <span className="text-amber-400">no match — filter ignored</span>
        )}
      </span>,
    );
  }
  if (userInput) {
    const u = lookup?.user;
    parts.push(
      <span key="user">
        <span className="text-gray-400">User:</span>{' '}
        {u ? (
          <span>
            <span className="text-emerald-400">{u.email ?? '(no email)'}</span>
            <span className="text-gray-500"> → {u._id.slice(0, 8)}…</span>
          </span>
        ) : (
          <span className="text-amber-400">no match — filter ignored</span>
        )}
      </span>,
    );
  }
  return (
    <div className="text-[11px] text-gray-500 flex items-center gap-4 flex-wrap">
      {parts.map((p, i) => (
        <span key={i}>{p}</span>
      ))}
    </div>
  );
}

function PerRequestTable({
  data,
  error,
  isLoading,
}: {
  data: PlatformPerfPerRequestResponse | null;
  error?: unknown;
  isLoading?: boolean;
}) {
  if (error && !data) return <ErrorBanner error={error} />;
  if (!data) {
    return <Text className="text-gray-500 text-sm">{isLoading ? 'Loading…' : 'No data.'}</Text>;
  }
  if (!data.requests) {
    return <Text className="text-gray-500 text-sm">No requests in this range.</Text>;
  }
  const rows = [
    { label: 'Request time', hint: 'last child FetchLog.createdAt − Reflection.createdAt', row: data.requestMsPerRequest },
    { label: 'Fetch sum / request', hint: 'total upstream work — overcounts hedges', row: data.fetchSumPerRequest },
    { label: 'Fetch max / request', hint: 'slowest single upstream attempt in the request', row: data.fetchMaxPerRequest },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-gray-500 text-xs">
          <tr>
            <th className="text-left py-1 pr-3" title="Metric — each row is a value rolled up PER Reflection.">Per request</th>
            <th className="text-right py-1 px-3" title="50th percentile of this per-request metric.">p50</th>
            <th className="text-right py-1 px-3" title="95th percentile — the user-facing tail metric.">p95</th>
            <th className="text-right py-1 px-3" title="99th percentile — worst case tail.">p99</th>
          </tr>
        </thead>
        <tbody className="text-gray-200">
          {rows.map((r) => (
            <tr key={r.label} className="border-t border-gray-800">
              <td className="py-1 pr-3">
                <div>{r.label}</div>
                <div className="text-gray-500 text-[11px]">{r.hint}</div>
              </td>
              <td className="py-1 px-3 text-right">{fmtMs(r.row.p50)}</td>
              <td className="py-1 px-3 text-right">{fmtMs(r.row.p95)}</td>
              <td className="py-1 px-3 text-right">{fmtMs(r.row.p99)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HedgeSummary({
  data,
  error,
  isLoading,
  delays,
}: {
  data: PlatformPerfHedgeResponse | null;
  error?: unknown;
  isLoading?: boolean;
  delays: number[];
}) {
  if (error && !data) return <ErrorBanner error={error} />;
  if (!data) return <Text className="text-gray-500 text-sm">{isLoading ? 'Loading…' : 'No data.'}</Text>;
  if (!data.groups) return <Text className="text-gray-500 text-sm">No fetches in this range.</Text>;

  const wonByPct = data.winnerByAttemptPct;
  const s = data.savings;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Hedge fire rate"
          value={fmtPercent(data.hedgeFireRate)}
          hint={`${fmtNumber(data.groups)} logical fetches · avg ${data.avgAttemptsPerGroup?.toFixed(2) ?? '—'} attempts`}
        />
        <Kpi
          label="Won by attempt #1"
          value={fmtPercent(wonByPct[1])}
          hint={`${fmtNumber(data.winnerByAttempt[1])} groups · primary good enough`}
        />
        <Kpi
          label="Won by hedge (#2 or #3)"
          value={fmtPercent((wonByPct[2] ?? 0) + (wonByPct[3] ?? 0))}
          hint={`#2 ${fmtPercent(wonByPct[2])} · #3 ${fmtPercent(wonByPct[3])}`}
        />
        <Kpi
          label="Avg wall time saved by hedge"
          value={fmtMs(s.avgSavedMs)}
          hint={
            s.sampleSize
              ? `p50 ${fmtMs(s.p50SavedMs)} · p95 ${fmtMs(s.p95SavedMs)} · helped ${fmtPercent(s.hedgeWinsThatActuallyHelpedRate)} of the time`
              : 'no hedge wins yet'
          }
        />
      </div>
      <Text className="text-gray-500 text-xs">
        Savings estimate uses the live OPENAI_HEDGE_DELAYS_MS values ({fmtDelays(delays)}).
        It assumes attempt #2 fires after delay[0] and attempt #3 after delay[1].
      </Text>
    </div>
  );
}

interface TuningSuggestion {
  level: 'healthy' | 'fires-too-often' | 'wasted' | 'tail-escapes' | 'low-volume';
  summary: string;
  suggestedDelay1: number | null;
  suggestedDelay2: number | null;
}

function suggestTuning(
  row: PlatformPerfHedgeByClassifier,
  delays: number[],
): TuningSuggestion {
  const fire = row.hedgeFireRate ?? 0;
  const wonByHedge = (row.winnerByAttemptPct[2] ?? 0) + (row.winnerByAttemptPct[3] ?? 0);
  const p95 = row.winnerP95;
  const p99 = row.winnerP99;
  const d1 = roundUpTo(p95, 50);
  const d2 = roundUpTo(p99, 100) ?? (d1 ? d1 * 3 : null);
  const curD1 = delayAt(delays, 0);
  const curD2 = delayAt(delays, 1);

  if (row.groups < 50) {
    return {
      level: 'low-volume',
      summary: `Only ${row.groups} logical fetches in window — wait for more data before tuning.`,
      suggestedDelay1: null,
      suggestedDelay2: null,
    };
  }
  if (p99 !== null && p99 > curD2) {
    return {
      level: 'tail-escapes',
      summary: `Tail escapes the hedge — winner p99 (${Math.round(p99)}ms) exceeds delay2 (${curD2}ms). Lowering delay2 toward ${d2 ?? '?'}ms catches more of the tail; investigating the upstream is usually the bigger lever.`,
      suggestedDelay1: d1,
      suggestedDelay2: d2,
    };
  }
  if (fire > 0.15) {
    return {
      level: 'fires-too-often',
      summary: `Hedges fire ${(fire * 100).toFixed(1)}% of the time — well above the 5% target. Raise delay1 to ~${d1 ?? '?'}ms to bring fire rate down (current ${curD1}ms is below typical request latency).`,
      suggestedDelay1: d1,
      suggestedDelay2: d2,
    };
  }
  if (fire > 0.05 && wonByHedge < 0.10) {
    return {
      level: 'wasted',
      summary: `Hedges fire ${(fire * 100).toFixed(1)}% of the time but only win ${(wonByHedge * 100).toFixed(1)}% of those races. Mostly wasted load. Raise delay1 toward p99 (~${d2 ?? '?'}ms).`,
      suggestedDelay1: d1,
      suggestedDelay2: d2,
    };
  }
  return {
    level: 'healthy',
    summary: `Healthy — fire rate ${(fire * 100).toFixed(1)}%, ${(wonByHedge * 100).toFixed(1)}% of races won by a hedge. Current ${curD1}/${curD2} delays sit well above winner p95 (${p95 !== null ? Math.round(p95) + 'ms' : '—'}).`,
    suggestedDelay1: d1,
    suggestedDelay2: d2,
  };
}

function HedgeTuningTable({
  data,
  error,
  isLoading,
  delays,
}: {
  data: PlatformPerfHedgeResponse | null;
  error?: unknown;
  isLoading?: boolean;
  delays: number[];
}) {
  if (error && !data) return <ErrorBanner error={error} />;
  if (!data) return <Text className="text-gray-500 text-sm">{isLoading ? 'Loading…' : 'No data.'}</Text>;
  if (data.byClassifier.length === 0) {
    return <Text className="text-gray-500 text-sm">No fetches in this range.</Text>;
  }

  const COLOR: Record<TuningSuggestion['level'], string> = {
    healthy: 'text-emerald-400',
    'fires-too-often': 'text-amber-400',
    wasted: 'text-amber-400',
    'tail-escapes': 'text-rose-400',
    'low-volume': 'text-gray-500',
  };
  const LABEL: Record<TuningSuggestion['level'], string> = {
    healthy: 'Healthy',
    'fires-too-often': 'Fires too often',
    wasted: 'Wasted',
    'tail-escapes': 'Tail escapes',
    'low-volume': 'Low volume',
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-gray-500 text-xs">
          <tr>
            <th className="text-left py-1 pr-3" title="Classifier key — one row per upstream classifier.">Classifier</th>
            <th className="text-right py-1 px-3" title="Logical fetches — one per (reflection, classifier) tuple.">Logical fetches</th>
            <th className="text-right py-1 px-3" title="p95 of the WINNING attempt only — user-perceived latency. Basis for suggested delay1.">winner p95</th>
            <th className="text-right py-1 px-3" title="p99 of the winning attempt only — basis for suggested delay2.">winner p99</th>
            <th className="text-right py-1 px-3" title="% of logical fetches where the hedge fired. Target ~5%.">Fire %</th>
            <th className="text-right py-1 px-3" title="Currently configured via OPENAI_HEDGE_DELAYS_MS env var.">Current delays</th>
            <th className="text-right py-1 px-3" title="Suggested first delay (round-up of winner p95 to nearest 50ms) and second delay (round-up of winner p99 to nearest 100ms).">Suggested</th>
            <th className="text-left py-1 pl-3" title="Rule-based assessment.">Verdict</th>
          </tr>
        </thead>
        <tbody className="text-gray-200">
          {data.byClassifier.map((r) => {
            const s = suggestTuning(r, delays);
            return (
              <tr key={r.classifierKey} className="border-t border-gray-800 align-top">
                <td className="py-1 pr-3 font-mono text-xs">{r.classifierKey}</td>
                <td className="py-1 px-3 text-right">{fmtNumber(r.groups)}</td>
                <td className="py-1 px-3 text-right">{fmtMs(r.winnerP95)}</td>
                <td className="py-1 px-3 text-right">{fmtMs(r.winnerP99)}</td>
                <td className="py-1 px-3 text-right">{fmtPercent(r.hedgeFireRate)}</td>
                <td className="py-1 px-3 text-right font-mono text-xs text-gray-400">
                  {delays.length ? fmtDelays(delays) : 'disabled'}
                </td>
                <td className="py-1 px-3 text-right font-mono text-xs">
                  {s.suggestedDelay1 !== null ? `${s.suggestedDelay1} / ${s.suggestedDelay2 ?? '?'}` : '—'}
                </td>
                <td className="py-1 pl-3">
                  <div className={`text-xs font-semibold ${COLOR[s.level]}`}>{LABEL[s.level]}</div>
                  <div className="text-gray-400 text-[11px] mt-0.5 max-w-md">{s.summary}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RequestDetailModal({
  reflectionId,
  onClose,
}: {
  reflectionId: string | null;
  onClose: () => void;
}) {
  const detail = usePlatformPerfRequestDetail(reflectionId);
  return (
    <Dialog open={reflectionId !== null} onClose={onClose} static={true}>
      <DialogPanel className="max-w-5xl bg-gray-900 ring-1 ring-gray-800">
        <Flex justifyContent="between" alignItems="center" className="mb-3">
          <Title className="text-gray-100">Reflection detail</Title>
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-2 py-1 rounded ring-1 ring-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
          >
            Close
          </button>
        </Flex>
        {detail.isLoading && !detail.data && (
          <Text className="text-gray-500 text-sm">Loading…</Text>
        )}
        {detail.error && (
          <Text className="text-rose-400 text-sm">
            {detail.error instanceof Error ? detail.error.message : 'Failed to load'}
          </Text>
        )}
        {detail.data && <RequestDetailBody data={detail.data} />}
      </DialogPanel>
    </Dialog>
  );
}

function RequestDetailBody({ data }: { data: PlatformPerfRequestDetail }) {
  const { reflection: r, totals: t, fetchGroups } = data;
  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-400 space-y-1 font-mono">
        <div>id: <span className="text-gray-200">{r._id}</span></div>
        <div>bundle: <span className="text-gray-200">{r.bundleKey ?? '—'}</span></div>
        <div>at: <span className="text-gray-200">{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</span></div>
        <div>org: <span className="text-gray-200">{r.orgId ?? '—'}</span> · user: <span className="text-gray-200">{r.userId ?? '—'}</span> · apiKey: <span className="text-gray-200">{r.apiKeyId ?? '—'}</span></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Request time" value={fmtMs(t.requestMs)} hint="last fetch − reflection.createdAt" />
        <Kpi label="Text length" value={fmtNumber(r.textLength)} hint="chars" />
        <Kpi label="Classifiers" value={fmtNumber(t.classifierCount)} hint={`${fmtNumber(t.fetchCount)} fetch attempts`} />
        <Kpi label="Fetch sum / max" value={`${fmtMs(t.fetchSumMs)} · ${fmtMs(t.fetchMaxMs)}`} hint="sum overcounts hedges" />
      </div>

      {r.labels.length > 0 && (
        <div>
          <Text className="text-gray-200 text-sm font-semibold mb-1">
            Labels ({r.labels.length})
          </Text>
          <div className="flex flex-wrap gap-1 text-xs">
            {r.labels.map((l, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded ring-1 ring-gray-700 bg-gray-800 text-gray-200"
                title={`${l.classifierName} · backend ${l.backend ?? '—'}`}
              >
                {l.classifier}: <span className="font-mono">{l.label}</span>
                {l.displayName && l.displayName !== l.label && <span className="text-gray-500"> ({l.displayName})</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <Text className="text-gray-200 text-sm font-semibold mb-1">
          Fetch groups ({fetchGroups.length})
        </Text>
        <Text className="text-gray-500 text-xs mb-2">
          One group per classifier. Multiple rows = the hedge fired; the row marked{' '}
          <span className="text-emerald-400">★ winner</span> resolved first and drove user-facing
          latency. Others were still in-flight when the race resolved and finished anyway.
        </Text>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left py-1 pr-2" title="FetchLog.classifierKey. '↳' rows are additional hedge attempts in the same logical fetch.">classifier</th>
                <th className="text-left py-1 pl-2" title="FetchLog.service — backend (openai / anthropic / phi).">service</th>
                <th className="text-right py-1 px-2" title="Attempt count in this group.">#</th>
                <th className="text-right py-1 px-2" title="FetchLog.hedgeAttempt — 1 = primary, 2+ = hedge.">attempt</th>
                <th className="text-right py-1 px-2" title="FetchLog.durationMs — duration of this individual attempt.">durationMs</th>
                <th className="text-right py-1 px-2" title="HTTP status code returned for this attempt.">code</th>
                <th className="text-left py-1 pl-2" title="success boolean. False = errored/timed out.">ok</th>
                <th className="text-left py-1 pl-2" title="usage.total_tokens if reported by the provider.">tokens</th>
                <th className="text-left py-1 pl-2" title="FetchLog.createdAt — moment the row was saved (right after the fetch resolved). Earliest among successful attempts wins.">at</th>
                <th className="text-left py-1 pl-2" title="Marked '★ winner' on the attempt that resolved first.">winner</th>
              </tr>
            </thead>
            <tbody className="text-gray-200">
              {fetchGroups.flatMap((g) =>
                g.attempts.map((a, i) => (
                  <tr
                    key={`${g.classifierKey}-${i}`}
                    className={`border-t border-gray-800 ${a.isWinner ? 'bg-emerald-900/10' : ''}`}
                  >
                    <td className="py-1 pr-2 font-mono">
                      {i === 0 ? g.classifierKey : <span className="text-gray-500">↳</span>}
                    </td>
                    <td className="py-1 pl-2 font-mono">{i === 0 ? (g.service ?? '—') : ''}</td>
                    <td className="py-1 px-2 text-right text-gray-500">{i === 0 ? g.attemptCount : ''}</td>
                    <td className="py-1 px-2 text-right">{a.hedgeAttempt ?? '—'}</td>
                    <td className="py-1 px-2 text-right">{fmtMs(a.durationMs)}</td>
                    <td className="py-1 px-2 text-right">{a.statusCode ?? '—'}</td>
                    <td className="py-1 pl-2">{a.success ? '✓' : <span className="text-rose-400">✗</span>}</td>
                    <td className="py-1 pl-2">
                      {a.usage && typeof a.usage === 'object' && 'total_tokens' in a.usage
                        ? fmtNumber(Number(a.usage.total_tokens))
                        : '—'}
                    </td>
                    <td className="py-1 pl-2 font-mono text-[10px] text-gray-500">
                      {a.createdAt ? new Date(a.createdAt).toLocaleTimeString() : '—'}
                    </td>
                    <td className="py-1 pl-2">
                      {a.isWinner ? <span className="text-emerald-400">★ winner</span> : ''}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
